import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  Badge,
  Icon,
  Flex,
  Link,
  Button,
} from "@chakra-ui/react";
import { CheckIcon, StarIcon } from "@chakra-ui/icons";
import { FaUsers, FaBuilding, FaRocket, FaCrown } from "react-icons/fa";

interface PricingPlan {
  id: string;
  name: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  icon: any;
  maxUsers: number;
  color: string;
}

const pricingPlans: PricingPlan[] = [
  {
    id: "free",
    name: "Free",
    price: 0,
    period: "месяц",
    description: "Для пробного запуска внутри одной команды",
    maxUsers: 3,
    icon: FaUsers,
    color: "green",
    features: [
      "База заказчиков/проектов/заявок/вакансий/кандидатов",
      "Роли по умолчанию (Админ, HRD, Руководитель подразделения, Рекрутер)",
      "Базовая аналитика: общая сводка вакансий + простая воронка",
      "Экспорт до 1 000 строк в Excel",
      "До 3 подразделений, до 1 канала подбора",
      "Хранилище до 1 ГБ вложений",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 1490,
    period: "место/месяц",
    description: "Оптимально для отдела подбора малого/среднего масштаба",
    maxUsers: 15,
    icon: FaBuilding,
    color: "blue",
    isPopular: true,
    features: [
      "Всё из Free, плюс:",
      "Расширенная аналитика (фильтры: даты/канал/подразделение/вакансия/рекрутер/источник)",
      "Экспорт в Excel без ограничений по объёму",
      "Конвертер резюме: 100 конвертаций/мес (с квотой)",
      "Настраиваемые справочники этапов/причин, автоэтап «Резюме направлено деливери»",
      "Поддержка по e-mail 8×5",
      "До 15 подразделений, до 3 каналов подбора",
      "Хранилище до 10 ГБ",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: 2990,
    period: "место/месяц",
    description: "Для компаний с несколькими потоками найма и партнёрскими каналами",
    maxUsers: 10,
    icon: FaRocket,
    color: "purple",
    features: [
      "Всё из Pro, плюс:",
      "Конвертер резюме: 1 000 конвертаций/мес (с квотой)",
      "API и вебхуки, аудит изменений, расширенный RBAC",
      "Импорт/экспорт справочников, шаблоны отчётов",
      "Приоритетная поддержка 8×5, онбординг команды",
      "Хранилище до 100 ГБ (расширяется опцией)",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 250000,
    period: "месяц",
    description: "Для крупных заказчиков и закрытых контуров",
    maxUsers: 0,
    icon: FaCrown,
    color: "orange",
    features: [
      "Всё из Business, плюс:",
      "SSO (SAML/OIDC — единый вход), SCIM/каталоги по договорённости",
      "On-prem/изолированный контур, HA (высокая доступность) по ТЗ",
      "Неизменяемые журналы (WORM) и расширенный аудит",
      "Квоты конвертера по договору (безлимит опционально)",
      "Выделенный менеджер, SLA 99,9%, обучение/док-набор",
      "Индивидуальные интеграции (ATS/CRM/телефония/кадровые системы)",
    ],
  },
];

export const PricingPage = () => {
  const bgGradient = useColorModeValue("linear(to-br, gray.50, blue.50, purple.50)", "linear(to-br, gray.900, blue.900, purple.900)");

  const cardBg = useColorModeValue("white", "gray.800");
  const popularCardBg = useColorModeValue("blue.50", "blue.900");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const priceTextColor = useColorModeValue("gray.600", "gray.400");
  const userCountColor = useColorModeValue("gray.500", "gray.500");

  return (
    <Box minH="100vh" bgGradient={bgGradient} position="relative">
      {/* Декоративные элементы фона */}
      <Box
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="100%"
        bgImage="radial-gradient(circle at 25% 25%, rgba(79, 70, 229, 0.1) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(147, 51, 234, 0.1) 0%, transparent 50%)"
        pointerEvents="none"
      />

      <Container maxW="7xl" py={20} position="relative">
        <VStack spacing={16}>
          {/* Заголовок */}
          <VStack spacing={4} textAlign="center" maxW="3xl">
            <Heading
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="800"
              bgGradient="linear(to-r, blue.500, purple.600)"
              bgClip="text"
              lineHeight="shorter"
            >
              Выберите идеальный тариф
            </Heading>
            <Text fontSize={{ base: "lg", md: "xl" }} color={useColorModeValue("gray.600", "gray.400")} maxW="2xl" lineHeight="tall">
              Выберите подходящий тариф для вашей команды. Цены на сайте — ориентиры «от». Итоговая смета уточняется в коммерческом предложении.
            </Text>
          </VStack>

          {/* Тарифные планы */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="100%">
            {pricingPlans.map((plan) => (
              <Box
                key={plan.id}
                bg={plan.isPopular ? popularCardBg : cardBg}
                borderRadius="2xl"
                p={8}
                position="relative"
                border={plan.isPopular ? "3px solid" : "1px solid"}
                borderColor={plan.isPopular ? `${plan.color}.400` : borderColor}
                boxShadow={plan.isPopular ? "2xl" : "lg"}
                transform={plan.isPopular ? "scale(1.05)" : "scale(1)"}
                transition="all 0.3s"
                minH="600px"
                _hover={{
                  transform: plan.isPopular ? "scale(1.07)" : "scale(1.02)",
                  boxShadow: "2xl",
                }}
              >
                {/* Популярный бейдж */}
                {plan.isPopular && (
                  <Badge
                    position="absolute"
                    top="-10px"
                    left="50%"
                    transform="translateX(-50%)"
                    colorScheme={plan.color}
                    fontSize="sm"
                    px={4}
                    py={2}
                    borderRadius="full"
                    textTransform="uppercase"
                    fontWeight="bold"
                  >
                    <Icon as={StarIcon} mr={1} />
                    Популярный
                  </Badge>
                )}

                <Flex direction="column" h="100%" justify="space-between">
                  {/* Основной контент */}
                  <VStack spacing={6} align="stretch">
                    {/* Иконка и название */}
                    <VStack spacing={3}>
                      <Box p={3} borderRadius="xl" bg={`${plan.color}.100`} color={`${plan.color}.600`}>
                        <Icon as={plan.icon} w={6} h={6} />
                      </Box>
                      <Heading size="md" textAlign="center" lineHeight="short" noOfLines={1}>
                        {plan.name}
                      </Heading>
                      <Text fontSize="sm" color={textColor} textAlign="center" lineHeight="short">
                        {plan.description}
                      </Text>
                    </VStack>

                    {/* Цена */}
                    <VStack spacing={2}>
                      <HStack align="baseline" justify="center">
                        <Text fontSize="3xl" fontWeight="bold" color={`${plan.color}.500`}>
                          {plan.price.toLocaleString("ru-RU")}
                        </Text>
                        <Text fontSize="m" color={priceTextColor}>
                          ₽/{plan.period}
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color={userCountColor}>
                        {plan.maxUsers === 0 ? "индивидуально" : `до ${plan.maxUsers} рабочих мест`}
                      </Text>
                    </VStack>

                    {/* Функции */}
                    <List spacing={3} flex="1">
                      {plan.features.map((feature, index) => (
                        <ListItem key={index} fontSize="sm">
                          <ListIcon as={CheckIcon} color={`${plan.color}.500`} w={4} h={4} />
                          {feature}
                        </ListItem>
                      ))}
                    </List>
                  </VStack>

                  {/* Кнопка в самом низу */}
                  <Button
                    as={Link}
                    href="https://forms.yandex.ru/cloud/6900d1b95056902df5cde748"
                    target="_blank"
                    rel="noopener noreferrer"
                    size="lg"
                    colorScheme={plan.color}
                    variant={plan.isPopular ? "solid" : "outline"}
                    borderRadius="xl"
                    mt={6}
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "lg",
                    }}
                    transition="all 0.2s"
                  >
                    {plan.id === "free"
                      ? "Подключить Free"
                      : plan.id === "pro"
                        ? "Запросить счёт Pro"
                        : plan.id === "business"
                          ? "Обсудить Business"
                          : "Запросить КП Enterprise"}
                  </Button>
                </Flex>
              </Box>
            ))}
          </SimpleGrid>

          {/* Дополнительная информация */}
          <VStack spacing={8} textAlign="center" maxW="4xl">
            <Box bg={cardBg} p={8} borderRadius="2xl" boxShadow="lg" border="1px solid" borderColor={useColorModeValue("gray.200", "gray.600")}>
              <VStack spacing={4}>
                <Heading size="lg">Дополнительные опции</Heading>
                <Text color={useColorModeValue("gray.600", "gray.400")}>Для тарифов Pro, Business и Enterprise доступны дополнительные опции:</Text>
                <VStack spacing={2} align="start" w="100%">
                  <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
                    • Доп. конвертации резюме: пакеты 500/2 000/10 000 в мес.
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
                    • Хранилище вложений: +100 ГБ, +1 ТБ
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
                    • Расширенная поддержка: 12×5 / 24×7, выделенный канал
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
                    • Кастомные отчёты/дашборды: по ТЗ
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
                    • Миграция данных/импорт историй: по ТЗ
                  </Text>
                </VStack>
                <Button
                  as={Link}
                  href="https://forms.yandex.ru/cloud/6900d1b95056902df5cde748"
                  target="_blank"
                  rel="noopener noreferrer"
                  colorScheme="blue"
                  size="lg"
                  mt={6}
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "lg",
                  }}
                  transition="all 0.2s"
                >
                  Оставить заявку
                </Button>
              </VStack>
            </Box>

            {/* Контакты */}
            {/* <VStack spacing={2}>
              <Text fontSize="sm" color={useColorModeValue("gray.500", "gray.400")}>
                Есть вопросы? Мы поможем выбрать подходящий тариф
              </Text>
              <Text fontSize="sm" fontWeight="semibold">
                Email: sales@globushunt.ru | Телефон: +7 (495) 123-45-67
              </Text>
            </VStack> */}
          </VStack>
        </VStack>
      </Container>

      {/* Футер */}
      <Box borderTop="1px solid" borderColor={useColorModeValue("gray.200", "gray.600")} py={8} mt={20}>
        <Container maxW="7xl">
          <Flex direction={{ base: "column", md: "row" }} justify="space-between" align="center" gap={4}>
            <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
              © {new Date().getFullYear()} ООО «ГЛОБУС ИТ». Все права защищены. ИНН 5260365605
            </Text>
            <HStack spacing={6}>
              <Link
                href="/login"
                fontSize="sm"
                color={useColorModeValue("blue.600", "blue.400")}
                _hover={{ color: useColorModeValue("blue.800", "blue.300") }}
              >
                Войти в систему
              </Link>
              <Link
                href="/instruction.pdf"
                target="_blank"
                rel="noopener noreferrer"
                fontSize="sm"
                color={useColorModeValue("blue.600", "blue.400")}
                _hover={{ color: useColorModeValue("blue.800", "blue.300") }}
              >
                Инструкция
              </Link>
            </HStack>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
};
